import { Close } from '@mui/icons-material';
import {
  Box,
  Grid,
  IconButton,
  Modal,
  Slide,
  Typography,
  useMediaQuery,
} from '@mui/material';
import LaunchTwoToneIcon from '@mui/icons-material/LaunchTwoTone';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import React, { useEffect, useState } from 'react';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { capitalizeFirstLetter, fetchStatusObj } from '../../shared/helper';
import PropTypes from 'prop-types';
import MessageIcon from '@mui/icons-material/Message';
import SecondaryBtnComponent from '../buttonComponent/SecondaryBtnComponent';
import { DEFAULT_NULL_REPLACEMENT } from '../../shared/constants';
import InfoIcon from '@mui/icons-material/Info';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';


const renderStatusView = (status, color) => {
  const statusObj =
    status && status !== '-'
      ? fetchStatusObj(status, color)
      : { status: '-', color: null };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {statusObj.color && (
        <FiberManualRecordIcon
          htmlColor={statusObj.color}
          sx={{ height: '12px', width: '12px', mr: 1 }}
        />
      )}
      <Typography variant="h5">{statusObj.status}</Typography>
    </Box>
  );
};

const redirectDocumentlink = (url, fileName) => {
  window.open(url, '_blank');
};

const renderDocumentLink = item => {
  if (!item || item === '-') {
    return DEFAULT_NULL_REPLACEMENT;
  }
  const arr = item?.split('/');
  const fileLength = arr.length;
  const fileName = arr[fileLength - 1];
  return (
    <Box
      sx={{
        color: '#036FCB',
        display: 'flex',
        alignItems: 'center',
        fontWeight: 500,
        fontSize: 16,
      }}>
      <Box
        component="span"
        sx={{ cursor: 'pointer' }}
        onClick={() => redirectDocumentlink(item, fileName)}>
        {fileName}
      </Box>
      <LaunchTwoToneIcon
        sx={{
          width: '15px',
          height: '15px',
          marginLeft: '8px',
          cursor: 'pointer',
        }}
        onClick={() => redirectDocumentlink(item, fileName)}
      />
    </Box>
  );
};

const ClassicPlan = () => {

  function createData(benefit, description) {
    return { benefit, description};
  }

  const rows = [
    createData('OTC benefits', 'Not covered'),
    createData('Fitness benefits', 'In-network: $0 copay'),
    createData('Transportation services', 'Not covered'),
    createData('Telehealth', `In-network: $0 copay *Limitations may apply`),
    createData('Chiropractic services', `In-network: $20 copay; Out-of-network: $50 copay`),
    createData('Alternative medicine', 'Not covered'),
  ];

  return (
    <TableContainer component={Paper}>
      <Table aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell sx={{color: '#828282'}}>Benefits</TableCell>
            <TableCell align="right" sx={{color: '#828282'}}>Description</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow
              key={row.benefit}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              <TableCell component="th" scope="row" sx={{fontWeight: 500}} >
                {row.benefit}
              </TableCell>
              <TableCell align="right">{row.description}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const VenturePlan = () => {
  return (
    <>
      <Typography variant="h5">
        Includes comprehensive medical, hospital and pharmacy benefits in
        addition to reducing members' monthly Medicare Part B premium. SCAN
        Venture offers a wide array of benefits not covered by Original
        Medicare, such as:
      </Typography>
      <ul>
        <li>
          <Typography variant="body2">Dental</Typography>
        </li>
        <li>
          <Typography variant="body2">Gym Membership</Typography>
        </li>
        <li>
          <Typography variant="body2">Vision</Typography>
        </li>
        <li>
          <Typography variant="body2">Hearing Aids</Typography>
        </li>
        <li>
          <Typography variant="body2">
            Quarterly allowance for Over-The-Counter products and medications
          </Typography>
        </li>
      </ul>
    </>
  );
};

const BenefitsTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#fff',
    maxWidth: 405,
    color: '#000',
    padding: '16px',
    border: '1px solid gray',
  },
}));

export const ServiceFlyout = ({
  isOpen = false,
  data,
  title,
  id,
  messages,
  role,
  icon,
  handleClose,
  category,
  children,
  showSecondaryButton,
  showBenefits,
  secondaryButtonTitle,
  handlerClickSecondaryButton,
  topItem,
  sx,
  img,
  ...rest
}) => {
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'), {
    noSsr: true,
  });
  const [isMobileBreakpoint, setIsMobileBreakpoint] = useState(isSmallScreen);
  useEffect(() => {
    if (!isSmallScreen) {
      setIsMobileBreakpoint(false);
    } else {
      setIsMobileBreakpoint(true);
    }
  }, [isSmallScreen]);

  return (
    <Modal
      keepMounted
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
      open={isOpen}
      onClose={handleClose}
      sx={{ border: 'none' }}>
      <Slide
        direction={isMobileBreakpoint ? 'up' : 'left'}
        in={isOpen}
        mountOnEnter
        unmountOnExit>
        <Box
          sx={{
            position: 'absolute',
            top: { sm: 24, xs: 88 },
            right: { sm: 24, xs: 'none' },
            bottom: { sm: 24, xs: 0 },
            width: { sm: 640, xs: '100%' },
            boxSizing: 'border-box',
            bgcolor: '#fff',
            p: { sm: 4, xs: 3 },
            borderRadius: { sm: '24px', xs: '24px 24px 0 0' },
            overflowX: 'hidden',
            ...sx,
          }}
          {...rest}>
          <Box sx={{ position: 'absolute', top: 28, right: 24 }}>
            <Close sx={{ cursor: 'pointer' }} onClick={handleClose} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'left',
              alignItems: 'flex-start',
            }}>
            {(icon && (
              <Box
                component="img"
                height="50px"
                width="34px"
                src={icon}
                alt={category}
                sx={{ mr: 2 }}
              />
            )) ||
              (img && (
                <Box
                  component="img"
                  height="56px"
                  width="56px"
                  src={img}
                  alt={category}
                  sx={{ mr: 2, borderRadius: '100%' }}
                />
              ))}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                paddingRight: '10px',
              }}>
              {title && (
                <Typography variant="title" data-testid="title">
                  {title}
                </Typography>
              )}
              {role && <Typography variant="flyoutRole">{role}</Typography>}
            </Box>
            {messages && (
              <MessageIcon
                sx={{
                  ml: 3,
                  width: 35,
                  height: 35,
                  border: '1px solid #828282',
                  padding: 0.5,
                  color: '#036FCB',
                  mb: 2,
                  borderRadius: '100%',
                }}></MessageIcon>
            )}
          </Box>
          <Box>{topItem}</Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            {showSecondaryButton ? (
              <SecondaryBtnComponent
                sx={{
                  mt: 2,
                  height: '32px',
                }}
                title={secondaryButtonTitle}
                onClick={() => {
                  handlerClickSecondaryButton(id, title);
                }}
              />
            ) : null}
            {showBenefits && (
              <BenefitsTooltip
                title={
                  // title.includes('Classic') ? <ClassicPlan /> : <VenturePlan />
                  <ClassicPlan />
                }>
                <IconButton
                  sx={{
                    borderRadius: '16px',
                    backgroundColor: '#fff',
                    border: '1px solid gray',
                    height: '32px',
                    mt: 2,
                  }}>
                  <Typography variant="h6" sx={{ marginRight: '5px' }}>
                    Benefits
                  </Typography>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </BenefitsTooltip>
            )}
          </Box>
          <Grid container sx={{ mt: 2 }}>
            {data?.map((item, index) => {
              if (item.hide) {
                return '';
              }
              return (
                <Grid item key={index} xs={item.width ?? 4} sx={{ mt: 3 }}>
                  {
                    <Typography variant="serviceColumnName">
                      {item.column_name}
                    </Typography>
                  }

                  {Array.isArray(item.value) ? (
                    <Box component="ul" sx={{ m: 0, p: 0, ml: 2 }}>
                      {item.value.map((val, index) => (
                        <li key={index} sx={{ lineHeight: '24px' }}>
                          {val}
                        </li>
                      ))}
                    </Box>
                  ) : item.column_name === 'Document' ||
                    item.column_name === 'document' ? (
                    <Box>{renderDocumentLink(item.value)}</Box>
                  ) : item.column_name === 'Data source' ||
                    item.column_name === 'Data Source' ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: '#E4E4E4',
                        color: '#1F1F1F',
                        boxSizing: 'border-box',
                        border: '1px solid #C0C0C0',
                        width: 100,
                        p: 1,
                        borderRadius: '10px',
                      }}>
                      <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ) : item.column_name === 'Status' ||
                    item.column_name === 'status' ||
                    item.show_status_icon ? (
                    renderStatusView(item.value, item.status_color)
                  ) : (
                    <Typography variant="h5">
                      {item.capitalize
                        ? capitalizeFirstLetter(item.value)
                        : item.value}
                    </Typography>
                  )}
                </Grid>
              );
            })}
          </Grid>
          <Box sx={{ mt: 3 }}>{children}</Box>
        </Box>
      </Slide>
    </Modal>
  );
};

ServiceFlyout.propTypes = {
  isOpen: PropTypes.bool,
  data: PropTypes.array,
  title: PropTypes.string,
  messages: PropTypes.bool,
  role: PropTypes.string,
  id: PropTypes.string,
  icon: PropTypes.string,
  img: PropTypes.string,
  handleClose: PropTypes.func,
  handlerClickSecondaryButton: PropTypes.func,
  category: PropTypes.string,
  children: PropTypes.node,
};
